import { Box, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material'
import { Modal } from '../../../customComponents/Modal'
import { PlanOrganizationTaskName } from '../../../../models/PlanOrganizationTask'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { AddRevisionRequests } from '../../../../services/AnnualPlanService'
import { type RevisionRequestRequest } from '../../../../models/requestObjects/AnnualPlanRequestObjects'

interface Props {
  planId: number
  onClose: () => void
  onConfirm: () => void
}

export const RevisionFeedbackModal = (props: Props): JSX.Element => {
  const [allPrioritiesChecked, setAllPrioritiesChecked] = useState(false)
  const [rcnOperationsChecked, setRcnOperationsChecked] = useState(false)
  const [priorityOneChecked, setPriorityOneChecked] = useState(false)
  const [priorityTwoChecked, setPriorityTwoChecked] = useState(false)
  const [priorityThreeChecked, setPriorityThreeChecked] = useState(false)
  const [priorityFourChecked, setPriorityFourChecked] = useState(false)
  const [allPrioritiesFeedback, setAllPrioritiesFeedback] = useState('')
  const [rcnOperationsFeedback, setRcnOperationsFeedback] = useState('')
  const [priorityOneFeedback, setPriorityOneFeedback] = useState('')
  const [priorityTwoFeedback, setPriorityTwoFeedback] = useState('')
  const [priorityThreeFeedback, setPriorityThreeFeedback] = useState('')
  const [priorityFourFeedback, setPriorityFourFeedback] = useState('')
  const [showCheckboxError, setShowCheckboxError] = useState(false)
  const [showTextError, setShowTextError] = useState(false)

  const handleAllPrioritiesChecked = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const checked = event.target.checked
    setAllPrioritiesChecked(checked)

    if (checked) {
      setPriorityOneChecked(false)
      setPriorityTwoChecked(false)
      setPriorityThreeChecked(false)
      setPriorityFourChecked(false)
    }
  }

  const handleRcnOperationsChecked = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const checked = event.target.checked
    setRcnOperationsChecked(checked)
  }

  const handlePriorityOneChecked = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const checked = event.target.checked
    setPriorityOneChecked(checked)

    handlePriorityChecked(checked)
  }

  const handlePriorityTwoChecked = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const checked = event.target.checked
    setPriorityTwoChecked(checked)

    handlePriorityChecked(checked)
  }

  const handlePriorityThreeChecked = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const checked = event.target.checked
    setPriorityThreeChecked(checked)

    handlePriorityChecked(checked)
  }

  const handlePriorityFourChecked = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const checked = event.target.checked
    setPriorityFourChecked(checked)

    handlePriorityChecked(checked)
  }

  const handlePriorityChecked = (checked: boolean): void => {
    if (checked) {
      setAllPrioritiesChecked(false)
    }
  }

  const handleConfirm = async (): Promise<void> => {
    if (!allPrioritiesChecked && !rcnOperationsChecked && !priorityOneChecked && !priorityTwoChecked && !priorityThreeChecked && !priorityFourChecked) {
      setShowCheckboxError(true)
      setShowTextError(false)
      toast.error('You must select at least one area for revision to continue')
      return
    }

    const revisionRequests: RevisionRequestRequest[] = []
    if (allPrioritiesChecked && allPrioritiesFeedback.trim() !== '') {
      revisionRequests.push({ taskName: PlanOrganizationTaskName.AllPriorities, revisionText: allPrioritiesFeedback })
    }

    if (rcnOperationsChecked && rcnOperationsFeedback.trim() !== '') {
      revisionRequests.push({ taskName: PlanOrganizationTaskName.RcnOperations, revisionText: rcnOperationsFeedback })
    }

    if (priorityOneChecked && priorityOneFeedback.trim() !== '') {
      revisionRequests.push({ taskName: PlanOrganizationTaskName.PriorityOne, revisionText: priorityOneFeedback })
    }

    if (priorityTwoChecked && priorityTwoFeedback.trim() !== '') {
      revisionRequests.push({ taskName: PlanOrganizationTaskName.PriorityTwo, revisionText: priorityTwoFeedback })
    }

    if (priorityThreeChecked && priorityThreeFeedback.trim() !== '') {
      revisionRequests.push({ taskName: PlanOrganizationTaskName.PriorityThree, revisionText: priorityThreeFeedback })
    }

    if (priorityFourChecked && priorityFourFeedback.trim() !== '') {
      revisionRequests.push({ taskName: PlanOrganizationTaskName.PriorityFour, revisionText: priorityFourFeedback })
    }

    if (revisionRequests.length === 0) {
      setShowCheckboxError(false)
      setShowTextError(true)
      toast.error('You must provide feedback for any selected area to continue')
      return
    }

    await AddRevisionRequests(props.planId, revisionRequests)
    toast.success('Revision details saved')
    props.onConfirm()
  }

  const anyChecked = allPrioritiesChecked || rcnOperationsChecked || priorityOneChecked || priorityTwoChecked || priorityThreeChecked || priorityFourChecked

  return <Modal
    open={true}
    confirmButtonText='Submit Feedback'
    onClose={props.onClose}
    onConfirm={handleConfirm}
    title='Needs Revision Feedback'
    bodyContent={
      <>
        <Typography variant='h5' sx={{ fontSize: '16px', fontWeight: 600 }}>
          Provide Feedback
        </Typography>

        <Typography variant='body1' sx={{ py: '1em' }}>
          Please select what part of the annual plan can/needs to be adjusted.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', border: showCheckboxError && !anyChecked ? '1px solid red' : 'none' }}>
          <FormControlLabel
            label={PlanOrganizationTaskName.RcnOperations}
            control={
              <Checkbox
                checked={rcnOperationsChecked}
                onChange={handleRcnOperationsChecked}
                data-testid='rcnOperationsCheckbox'
                sx={{ py: 0 }}
              />
            }
          />
          {rcnOperationsChecked &&
            <TextField
              multiline={true}
              minRows={4}
              placeholder='What needs to be adjusted?...'
              variant='filled'
              error={showTextError && rcnOperationsFeedback.trim() === ''}
              value={rcnOperationsFeedback}
              data-testid='rcnOperationsFeedback'
              onChange={(event) => { setRcnOperationsFeedback(event.target.value) }}
              sx={{ borderRadius: '6px', my: '1em' }}
            />
          }

          <FormControlLabel
            label={PlanOrganizationTaskName.AllPriorities}
            control={
              <Checkbox
                checked={allPrioritiesChecked}
                onChange={handleAllPrioritiesChecked}
                data-testid='allPrioritiesCheckbox'
                sx={{ py: 0 }}
              />
            }
          />
          {allPrioritiesChecked &&
            <TextField
              multiline={true}
              minRows={4}
              placeholder='What needs to be adjusted?...'
              variant='filled'
              error={showTextError && allPrioritiesFeedback.trim() === ''}
              value={allPrioritiesFeedback}
              data-testid='allPrioritiesFeedback'
              onChange={(event) => { setAllPrioritiesFeedback(event.target.value) }}
              sx={{ borderRadius: '6px', my: '1em' }}
            />
          }

          <FormControlLabel
            label={PlanOrganizationTaskName.PriorityOne}
            control={
              <Checkbox
                checked={priorityOneChecked}
                onChange={handlePriorityOneChecked}
                data-testid='priorityOneCheckbox'
                sx={{ py: 0 }}
              />
            }
          />
          {priorityOneChecked &&
            <TextField
              multiline={true}
              minRows={4}
              placeholder='What needs to be adjusted?...'
              variant='filled'
              error={showTextError && priorityOneFeedback.trim() === ''}
              value={priorityOneFeedback}
              data-testid='priorityOneFeedback'
              onChange={(event) => { setPriorityOneFeedback(event.target.value) }}
              sx={{ borderRadius: '6px', my: '1em' }}
            />
          }

          <FormControlLabel
            label={PlanOrganizationTaskName.PriorityTwo}
            control={
              <Checkbox
                checked={priorityTwoChecked}
                onChange={handlePriorityTwoChecked}
                data-testid='priorityTwoCheckbox'
                sx={{ py: 0 }}
              />
            }
          />
          {priorityTwoChecked &&
            <TextField
              multiline={true}
              minRows={4}
              placeholder='What needs to be adjusted?...'
              variant='filled'
              error={showTextError && priorityTwoFeedback.trim() === ''}
              value={priorityTwoFeedback}
              data-testid='priorityTwoFeedback'
              onChange={(event) => { setPriorityTwoFeedback(event.target.value) }}
              sx={{ borderRadius: '6px', my: '1em' }}
            />
          }

          <FormControlLabel
            label={PlanOrganizationTaskName.PriorityThree}
            control={
              <Checkbox
                checked={priorityThreeChecked}
                onChange={handlePriorityThreeChecked}
                data-testid='priorityThreeCheckbox'
                sx={{ py: 0 }}
              />
            }
          />
          {priorityThreeChecked &&
            <TextField
              multiline={true}
              minRows={4}
              placeholder='What needs to be adjusted?...'
              variant='filled'
              error={showTextError && priorityThreeFeedback.trim() === ''}
              value={priorityThreeFeedback}
              data-testid='priorityThreeFeedback'
              onChange={(event) => { setPriorityThreeFeedback(event.target.value) }}
              sx={{ borderRadius: '6px', my: '1em' }}
            />
          }
          <FormControlLabel
            label={PlanOrganizationTaskName.PriorityFour}
            control={
              <Checkbox
                checked={priorityFourChecked}
                onChange={handlePriorityFourChecked}
                data-testid='priorityFourCheckbox'
                sx={{ py: 0 }}
              />
            }
          />
          {priorityFourChecked &&
            <TextField
              multiline={true}
              minRows={4}
              placeholder='What needs to be adjusted?...'
              variant='filled'
              error={showTextError && priorityFourFeedback.trim() === ''}
              value={priorityFourFeedback}
              data-testid='priorityFourFeedback'
              onChange={(event) => { setPriorityFourFeedback(event.target.value) }}
              sx={{ borderRadius: '6px', my: '1em' }}
            />
          }
        </Box>
      </>
    }
  />
}
